import { useMemo, useState } from "react";
import styled from "styled-components";
import { CaretDownIcon } from "../../../../../components/Icons/Icons";
import { H3 } from "../../../../../components/Typography/Typography";
import { AttributeValueDisplay } from "../../../ProductDetailPage/ProductOverview/product_overview.utils";
import type {
  AttributeSchema,
  PIMProduct,
} from "../../../../../types/types.PIM";
import {
  findCollectionByName,
  getAlignedCollectionColumns,
  getAlignedCollectionItems,
  getCollectionAttributeForProduct,
  MISSING_VALUE_DISPLAY,
} from "./CompareProducts.util";

const Section = styled.div`
  border-bottom: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")} 0;
`;

const SectionHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  width: 100%;
  text-align: left;
`;

const Caret = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  transform: ${({ $expanded }) =>
    $expanded ? "rotate(0deg)" : "rotate(-90deg)"};
  transition: transform 0.15s ease-out;
`;

const ItemList = styled.div`
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const ItemRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  width: 100%;
  background: ${({ theme }) => theme.resolve("color_bg_surface_subtle")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_lg")};
  cursor: pointer;
  text-align: left;
`;

const ItemLabel = styled.span`
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const DetailMatrix = styled.div<{ $cols: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
  gap: ${({ theme }) => theme.resolve("spacing_lg")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-top: none;
  border-radius: 0 0 ${({ theme }) => theme.resolve("radius_md")}
    ${({ theme }) => theme.resolve("radius_md")};
  margin-top: -4px;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
`;

const AttributeBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xl")};
  min-width: 0;
  overflow: hidden;
`;

const AttributeLabel = styled.div`
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  max-width: 100%;
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const EmptyValue = styled.span`
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
`;

type CompareCollectionSectionProps = {
  collectionName: string;
  displayName: string;
  products: PIMProduct[];
  expanded: boolean;
  onToggle: () => void;
  keyAttributeName?: string;
};

function scopeAttributeForProduct(
  attribute: AttributeSchema,
  productId: string
): AttributeSchema {
  return {
    ...attribute,
    name: `${attribute.name}__${productId}`,
    values: attribute.values ? attribute.values.map((v) => ({ ...v })) : [],
  };
}

export function CompareCollectionSection({
  collectionName,
  displayName,
  products,
  expanded,
  onToggle,
  keyAttributeName,
}: CompareCollectionSectionProps) {
  const items = useMemo(
    () => getAlignedCollectionItems(products, collectionName, keyAttributeName),
    [products, collectionName, keyAttributeName]
  );
  const columns = useMemo(
    () =>
      getAlignedCollectionColumns(products, collectionName, keyAttributeName),
    [products, collectionName, keyAttributeName]
  );
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  if (columns.length === 0) {
    return null;
  }

  const toggleItem = (itemId: string) => {
    setOpenItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  return (
    <Section>
      <SectionHeader type="button" onClick={onToggle}>
        <Caret $expanded={expanded}>
          <CaretDownIcon width={16} height={16} />
        </Caret>
        <H3 style={{ margin: 0 }}>{displayName}</H3>
      </SectionHeader>
      {expanded && (
        <ItemList>
          {items.length === 0 ? (
            <EmptyValue>{MISSING_VALUE_DISPLAY}</EmptyValue>
          ) : (
            items.map((item) => {
              const isOpen = !!openItems[item.id];
              return (
                <div key={item.id}>
                  <ItemRow type="button" onClick={() => toggleItem(item.id)}>
                    <Caret $expanded={isOpen}>
                      <CaretDownIcon width={14} height={14} />
                    </Caret>
                    <ItemLabel>{item.rowKey}</ItemLabel>
                  </ItemRow>
                  {isOpen && (
                    <div>
                      {columns.map((colDef) => (
                        <DetailMatrix
                          key={colDef.id || colDef.name}
                          $cols={products.length}
                        >
                          {products.map((product) => {
                            const collection = findCollectionByName(
                              product,
                              collectionName
                            );
                            const row = item.rowsByProductId[product.id];
                            const attribute = getCollectionAttributeForProduct(
                              collection,
                              row,
                              colDef
                            );
                            return (
                              <AttributeBlock
                                key={`${product.id}-${colDef.name}`}
                                data-product-id={product.id}
                                data-attribute={colDef.name}
                              >
                                <AttributeLabel
                                  title={colDef.display_name || colDef.name}
                                >
                                  {colDef.display_name || colDef.name}
                                </AttributeLabel>
                                {attribute ? (
                                  <AttributeValueDisplay
                                    attribute={scopeAttributeForProduct(
                                      attribute,
                                      product.id
                                    )}
                                    isPortfolio={false}
                                    max_line_clamp={4}
                                  />
                                ) : (
                                  <EmptyValue>
                                    {MISSING_VALUE_DISPLAY}
                                  </EmptyValue>
                                )}
                              </AttributeBlock>
                            );
                          })}
                        </DetailMatrix>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </ItemList>
      )}
    </Section>
  );
}
